import config from "../config";
import _ from "lodash";
import {addSessionTokenToRequestHeader} from "../services/fetchInterceptor.service";
import UserController from "./UserController";
import objectUtil from "../util/objectUtil";
import FileUtil from "../util/FileUtil";

class APIController{

    static WORKSHEET_STATES = {
        "DRAFT_WORKING_SHEET" : "DRAFT_WORKING_SHEET",
        "OPEN_WORKING_SHEET" : "OPEN_WORKING_SHEET",
        "SUBMITTED_WORKING_SHEET" : "SUBMITTED_WORKING_SHEET",
        "GRADED_WORKING_SHEET" : "GRADED_WORKING_SHEET",
        "DISCARDED_WORKING_SHEET" : "DISCARDED_WORKING_SHEET",
    }

    /**
     * Generic fetch function with includes standard headers
     * @param {string} urlPath
     * @param {string} method
     * @param {object} body
     * @param {object} extraHeaders
     * @param {URLSearchParams} params
     * @returns
     */
    static async apiFetch(urlPath, method = "GET", body = null,  params = null, extraHeaders = {}) {
        // URL mit Query-Params zusammenbauen, falls vorhanden
        let fullUrlPath = config.API_HOST_URL + urlPath;
        if (params instanceof URLSearchParams && [...params].length > 0) {
            fullUrlPath = config.API_HOST_URL + `${urlPath}?${params.toString()}`;
        }

        const headers = {
            "Content-Type": "application/json",
            ...extraHeaders, // optionale Header hinzufügen oder überschreiben
        };

        const options = {
            method,
            headers,
            credentials: 'include', // Cookies automatisch mitschicken
        };

        if (body && method !== "GET") { // GET-Anfragen haben normalerweise keinen Body
            options.body = JSON.stringify(body);
        }

        const resp = await fetch(fullUrlPath, options);


        if (resp.status !== 200) {

            if(resp.status == 401){
                if(UserController.isLoggedIn()){
                    window.alert("Login Sitzung abgelaufen. Bitte erneut einloggen.");
                    UserController.logout();
                }
                window.location.reload();
                return null;
            }else{
                const text = await resp.text();
                throw new Error(`HTTP ${resp.status}: ${text}`);
            }

        }

        // JSON parsen, falls möglich
        try {
            return await resp.json();
        } catch {
            return null; // keine JSON-Antwort
        }
    }

    static async addWorkingsheetTemplate(topicId, templateId = null){
        let urlParams = new URLSearchParams();
        urlParams.set('projectId',topicId);
         if(templateId != null){
            urlParams.set('templateId',templateId);
        }
        return await this.apiFetch("/projects","PUT",{},urlParams);
    }

    static async updateWorkingsheetTemplate(project){
        return await this.apiFetch("/projects","POST",project);
    }

    static async deleteWorkingsheetTemplate(projectId){
        let urlParams = new URLSearchParams({projectId:projectId});
        return await this.apiFetch("/projects","DELETE",{},urlParams);
    }

    static async getWorksheetById(worksheetId){
        let timestamp = new Date().getTime(); //Cache-Busting to fix strange cache behavior
        const urlParams = new URLSearchParams({
            worksheetId: worksheetId,
            timestamp: timestamp
        });
        return await this.apiFetch("/worksheets","GET",{},urlParams);
    }

    static async updateProjectFiles(projectId, projectFilesArray){
        const params = new URLSearchParams({
            projectId: projectId
        });
        return await this.apiFetch("/projects/files","POST",projectFilesArray,params);
    }


    static async getAllProjectTemplatesByProfessionId(professionId){
        let projects = await this.apiFetch("/projects/templates","GET");
        if(projects.length > 0){
            return projects.filter(p=>p.professionId === professionId)
        }else{
            return [];
        }
    }

    static async getProjectTemplates(){
        return await this.apiFetch("/projects/templates","GET");
    }

    static async getPDFFile(worksheetId){
       const resp = await fetch(`${config.API_HOST_URL}/worksheets/printpdf?worksheetId=`+worksheetId, {
            method: "GET",
            credentials: 'include'
        });
        if(!resp.ok){
            throw Error(resp.statusText);
        }
        let blobResp = await resp.blob();
        let url = URL.createObjectURL(blobResp);
        return url;
    }

    static async downloadPDFFile(worksheetId, traineeId, courseId, topicId){
       const resp = await fetch(`${config.API_HOST_URL}/worksheets/printpdf?worksheetId=`+worksheetId, {
            method: "GET",
            credentials: 'include'
        });
        if(!resp.ok){
            throw Error(resp.statusText);
        }
        let blobResp = await resp.blob();
        let url = URL.createObjectURL(blobResp);
        const trainee = await APIController.getTraineeById(traineeId);
        let fileName = trainee.name+"_"+trainee.groupName+"_"+courseId+"_"+topicId+".pdf";
        // Temporären Link erstellen und simulierten Download starten
        const tempLink = document.createElement("a");
        tempLink.href = url;
        tempLink.download = fileName; // Name der heruntergeladenen Datei
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink); // Bereinigen
        URL.revokeObjectURL(url); // URL freigeben
        return url;
    }

    static async getUserProfile(){
        return await this.apiFetch("/users/me","GET");
    }

    static async getSessionUser(){
        return await this.apiFetch("/users/getSessionUser","GET");
    }

    static async refreshSessionUser() {
        let result = await this.apiFetch("/users/refreshlogin","GET");
        if(!result){
            throw new Error("login refresh failed");
        }else{
            return true;
        }
    }

    static async logoutSessionUser() {
        return await this.apiFetch("/users/logout","GET");
    }

    static async loginAs(userId){
        const params = new URLSearchParams();
        params.append("userId", userId);
        return await this.apiFetch( `/users/loginAs`,"GET",{},params);;
    }

    static async switchLogin(userId){
        const params = new URLSearchParams();
        params.append("userId", userId);
        return await this.apiFetch( `/users/switchLogin`,"GET",{},params);;
    }

    static async verifyTAN(TAN){
        let resp = await fetch( `${config.API_HOST_URL}/users/verify/tan/${TAN}`);
        let jsonRes = await resp.json();
        if(!resp.ok){
            throw Error(jsonRes.message);
        }
        return jsonRes;
    }

    static async requestVerificationCode(email, tan=null){
        let url = `${config.API_HOST_URL}/users/requestCode/${encodeURIComponent(email)}${tan !== null ? "?tan="+encodeURIComponent(tan) : ""}`;
        let resp = await fetch(url);
        let jsonRes = await resp.json();
        if(!resp.ok){
            throw Error(jsonRes.message);
        }
        return jsonRes;
    }

    static async requestForgotPasswordEmail(email){
        const postHeaders = new Headers();
        postHeaders.append("Content-Type", "application/json");
        const raw = JSON.stringify({
            "email": email
        });
        let resp = await fetch(`${config.API_HOST_URL}/users/forgotPassword/requestCode`, {
            method: "POST",
            headers: postHeaders,
            body: raw
        });
        let jsonRes = await resp.json();
        if(!resp.ok){
            throw Error(jsonRes.message);
        }
        return jsonRes;
    }

    static async resetPassword(password, nuance){
        const postHeaders = new Headers();
        postHeaders.append("Content-Type", "application/json");
        const raw = JSON.stringify({
            "password": password,
            "nuance": nuance
        });
        let resp = await fetch(`${config.API_HOST_URL}/users/forgotPassword/setPassword`, {
            method: "POST",
            headers: postHeaders,
            body: raw
        });
        let jsonRes = await resp.json();
        if(!resp.ok){
            throw Error(jsonRes.message);
        }
        return jsonRes;
    }

    static async updatePassword(oldPassword, newPassword){
        let body = {
            "oldPassword": oldPassword,
            "newPassword": newPassword
        };
        return await this.apiFetch("/users/refreshlogin","POST",body);
    }

    static async verifyVerificationCode(email, body){
        return await this.apiFetch(`$/users/verifyCode/${email}`,"POST",body);
    }

    static async resetInitialEmailAndInitialPassword(email, tan, password, consents){
        let body = {
            "email": email,
            "tan": tan,
            "password": password,
            "consents": consents
        };
        return await this.apiFetch("/users/initialSetUpAccount","POST",body);
    }

    static async getTraineeProfile(traineeId,showWarning = false){
        const params = new URLSearchParams({
            id: traineeId
        });
        let jsonRes = await this.apiFetch("/trainees/profile","GET",{},params);
        localStorage.removeItem("showProfileWarning");
        let isFinished = await objectUtil.isTraineeProfileFinished(jsonRes);
        if(showWarning && !isFinished){
            localStorage.setItem("showProfileWarning",'true');
        }
        return jsonRes;
    }


    static async upsertImageAndVideoConsent(accepted = false){
        if(!UserController.isTrainee()){
            return null;
        }
        const params = new URLSearchParams({
            accepted: accepted
        });
        return await this.apiFetch("/trainees/imageAndVideoConsent","PUT",{},params);
    }

    static async updateTraineeProfile(profileData){
        if(!UserController.isTrainee()){
            return null;
        }
        const requestOptions = {
            redirect: "follow", //???? TODO
        };
        return await this.apiFetch("/trainees/profile","POST",profileData);
    }


    static async getPublicWorksheetById(worksheetId){
        let timestamp = new Date().getTime(); //Cache-Busting to fix strange cache behavior
        const params = new URLSearchParams({
            worksheetId: worksheetId,
            timestamp: timestamp
        });
        return await this.apiFetch("/worksheets/public","GET",{},params);
    }

    static async getTraineeById(traineeId){
        let timestamp = new Date().getTime(); //Cache-Busting to fix strange cache behavior
        const urlParams = new URLSearchParams();
        urlParams.set("id",traineeId);
        urlParams.set("timestamp",timestamp)
        return await this.apiFetch("/trainees","GET",{},urlParams);
    }

    static async getWorksheetByParam(param, value, extra= {}){
        let timestamp = new Date().getTime(); //Cache-Busting to fix strange cache behavior
        const urlParams = new URLSearchParams();
        urlParams.set(param, value);
        urlParams.set('timestamp', timestamp);
        return await this.apiFetch("/worksheets","GET",{},urlParams);
    }

    static async getAllTopicsGroupedByProfessionId(professionId, showDetails = false, patternFilter = []){
        const urlParam = new URLSearchParams();
        urlParam.set('professionId',professionId);
        urlParam.set('showDetails',(showDetails?"1":"0"));
        patternFilter.forEach((filter, index) => {
            urlParam.append('topicIdRegex', filter);
        });
        return await this.apiFetch("/topics/getAllTopicsGroupedByProfessionId","GET",{}, urlParam);
    }

    static async getTopicBlacklist(){
        return await this.apiFetch("/topics/blacklist");
    }


    static async getAllProfessionNamesFromTopics(){
        return await this.apiFetch("/topics/getAllProfessionNames");
    }


    /**
     * Uploads a file, but first make some checks
     * @param file file-object how it is coming from dropzone onDrop
     * @param maxAllowedSize max default size 10MB
     * @param allowedExtensions default ['.pdf','.jpg',...]
     * @returns {Promise<null>}
     */
    static async uploadFile(file, maxAllowedSize = (10 * 1024 * 1024), allowedExtensions = FileUtil.getExtensionWhitelist()){
        let fileExtension = FileUtil.getFileExtension(file.path);
        if(!allowedExtensions.includes(fileExtension)){
            window.alert("Dateityp nicht auf der Whitelist!");
            return null;
        }else if( FileUtil.isDangerousFile(file.path)){
            window.alert("Dateityp nicht erlaubt!");
            return null;
        }else if(file.size >= maxAllowedSize){
            window.alert("Datei ist zu groß!");
            return null;
        }else{
            let formData = new FormData();
            formData.append('file',file)
             let response = await fetch(
                    config.API_HOST_URL+"/files/upload",
                    {
                        credentials: "include",
                        method: "POST",
                        body: formData
                    }
                );
            return await response.json();
        }
    }

    static async getAllTopics(showDetails=false,professionId=null){
        const urlParam = new URLSearchParams();
        if(professionId != null){
            urlParam.set('professionId',professionId);
        }
        urlParam.set('showDetails',(showDetails?"1":"0"));
        return await this.apiFetch("/topics/getAllTopicsGroupedByProfessionId","GET",{},urlParam);
    }

    /*
    static async migrateAllWorksheets(){
        return await this.apiFetch("/worksheets/migrate");
    }
    */

    static async deleteAllWorksheetsByCourseId(courseId){
        const urlParam = new URLSearchParams();
        urlParam.set('courseId',courseId);
        return await this.apiFetch("/worksheets/migrate","DELETE",{},urlParam);
    }

    static async getEvaluationByWorksheetId(worksheetIds = []){
        if (!Array.isArray(worksheetIds)) {
            worksheetIds = [worksheetIds];
        }
        const urlParam = new URLSearchParams();
        worksheetIds.forEach((worksheetId, index) => {
            urlParam.append('worksheetId', worksheetId);
        });
        return await this.apiFetch("/worksheets/evaluate","GET",{},urlParam);
    }

    static async getEvaluationsByCourseAndTopic(courseId, topic){
        const urlParam = new URLSearchParams();
        urlParam.set('courseId',courseId);
        urlParam.set('topic',topic);
        return await this.apiFetch("/worksheets/evaluate","GET",{},urlParam);
    }

    /**
     *
     * @param courseId
     * @param topic
     * @param evaluations { "traineeId" => evaluation, "traineeId2" => evaluation2, }
     * @returns {Promise<any>}
     */
    static async updateEvaluationsByCourseAndTopic(courseId, topic, evaluations){
        const urlParam = new URLSearchParams();
        urlParam.set('courseId',courseId);
        urlParam.set('topic',topic);
        return await this.apiFetch("/worksheets/evaluate","POST",evaluations,urlParam);
    }


    static async getEvaluationsByWorksheet(worksheet){
        addSessionTokenToRequestHeader();
        const urlParam = new URLSearchParams();
        urlParam.set('courseId',worksheet.courseId);
        urlParam.set('topic',worksheet.topic);
        let evaluations = await this.apiFetch("/worksheets/evaluate","GET",{},urlParam);
        return evaluations[worksheet.traineeId];


    }


    static async getCourseById( courseId, extra= {}){
        let timestamp = new Date().getTime(); //Cache-Busting to fix strange cache behavior
        let queryParams = {
            id: courseId,
            timestamp: timestamp
        };
        let params = new URLSearchParams(queryParams);
        return await this.apiFetch("/courses","GET",{},params);
    }

    static async getCoursesList(params){
        return await this.apiFetch("/courses","GET",{},params);
    }

    static async syncCourseWithParams(params){
        let resp = await this.apiFetch("/courses/sync","GET",{},params);
        console.info("Sync request received");
        return {status: true};
    }

    static async assignWorkingsheet( courseId, traineeId, topicId, extra={}){
        let queryParams = {
            courseId: courseId,
            traineeId: traineeId,
            topicId: topicId
        };
        let params = new URLSearchParams(queryParams);
        return await this.apiFetch("/worksheets/instructor/assign","GET",{},params);
    }


    static async updateWorksheetV2(worksheet){
        return await this.apiFetch("/worksheets","PUT",worksheet);
    }

    static async updateWorksheetState(worksheet){
        return await this.apiFetch("/worksheets/state","PUT",worksheet);
    }

    static async _updateWorksheet(worksheet,worksheetState = null){
        let params = null;
        if(worksheetState != null){
            params = new URLSearchParams({
                updateState: worksheetState
            });
        }
        return await this.apiFetch("/worksheets","PUT",worksheet,params);
    }

    static async asssignNewWorksheet(courseId, topicId, instructorId, traineeId, onFinish,onError){
        let params = new URLSearchParams({
            courseId: courseId,
            topicId: topicId,
            instructorId: instructorId,
            traineeId: traineeId
        });
        return await this.apiFetch("/worksheets","POST",{},params);
    }

    static async deleteWorksheet(worksheetId,forceDelete=false){
        let params = new URLSearchParams({
            worksheetId: worksheetId,
            forceDelete: forceDelete
        });
        return await this.apiFetch("/worksheets","DELETE",{},params);
    }


    static async updateWorksheet(worksheet){
        return APIController._updateWorksheet(worksheet,null)
    }

    static async updateWorksheetComments(worksheet){
        return await this.apiFetch("/worksheets/comments","PUT",worksheet);
    }

    static async updateWorksheetAndPhase(worksheet){
        addSessionTokenToRequestHeader();
        let params = new URLSearchParams({
            updatePhase: true
        });
        return await this.apiFetch("/worksheets","PUT",worksheet,params);
    }


    /**
     *
     * @param topicFilterPatternArray like ['*-17-1']
     * @param onFinish
     * @param onError
     * @returns {Promise<void>}
     */
    static async getAllProjectsByTopicFilterPattern(topicFilterPatternArray = [],onFinish,onError){
        let params = new URLSearchParams();
        topicFilterPatternArray.forEach((topic, i) => {
            params.append("topicIdRegex",topic)
        });
        return await this.apiFetch("/projects","GET",{},params);
    }

    static async getAllProjectsByProfessionId(professionId){
        let params = new URLSearchParams({
            professionId: professionId
        });
        return await this.apiFetch("/projects","GET",{},params);
    }



    static async sendWorksheetTemplateBulkResetDelete(topicIds = []){
        let params = new URLSearchParams();
        topicIds.forEach((topic, i) => {
            params.append("topicIds",topic)
        });
        return await this.apiFetch("/projects/bulksetup","DELETE",{},params);
    }

    static async sendWorksheetTemplateBulkReset(professionId,templateId, deleteOldFileteredByTopics=false,
                                                addImageComponentInFirstSection=false,topicIds = []){
        let params = new URLSearchParams({
            professionId: professionId,
            templateId: templateId,
            deleteOldFileteredByTopics: deleteOldFileteredByTopics,
            addImageComponentInFirstSection: addImageComponentInFirstSection
        });
        topicIds.forEach((topic, i) => {
            params.append("topicIds",topic)
        });
        return await this.apiFetch("/projects/bulksetup","GET",{},params);
    }

    static async getInstructorByParams(params){
        return await this.apiFetch("/instructors","GET",{},params);
    }

    static async getProjectsByParams(params = new URLSearchParams()){
        return await this.apiFetch("/projects","GET",{},params);
    }

    static async getSyncLogs(){
        return await this.apiFetch("/admin/sync/readInfo");
    }

    static async requestSync(){
        return await this.apiFetch("/admin/sync");
    }

    static async cloneTemplates(projectId){
        return await this.apiFetch(`/projects/templates/clone/${projectId}`,"POST");
    }

    static async getTopicsListAdmin(params){
        return await this.apiFetch("/admin/topics","GET",{},params);
    }

    static async getCoursesListAdmin(params){
        return await this.apiFetch("/admin/courses","GET",{},params);
    }

    static async getInstructorsListAdmin(params){
        return await this.apiFetch("/admin/instructors","GET",{},params);
    }
    static async getTraineesListAdmin(params){
        return await this.apiFetch("/admin/trainees","GET",{},params);
    }

    static async createTemplate(parentProjectId = null, professionId = null){
        let params = {};
        params.isTemplate = true;
        if(parentProjectId != null){
            params.templateId = parentProjectId;
        }
        if(professionId != null){
            params.professionId = professionId;
        }
        let urlParams = new URLSearchParams(params);
        return await this.apiFetch("/projects","PUT",{},urlParams);
    }

    static async sendInviteSupervisor(email, accessType){
        return await this.apiFetch("/trainees/profile/invite","POST",{email: email, accessType: accessType});
    }

    static async deleteInvite(email){
        return await this.apiFetch("/trainees/profile/invite","DELETE",{email: email});
    }

    static async removeSupervisor(email){
        return await this.apiFetch("/trainees/profile/connection","PUT",{email: email});
    }

    static async getSingleSupervisorById(id){
        let params = new URLSearchParams();
        params.set('id',id);
        return await this.apiFetch("/supervisors","GET",{},params);
    }

}
export default APIController;
